import { useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import { articles } from "@/mocks/articles";

const categoryNames: Record<string, string> = {
  action: "动作片",
  comedy: "喜剧片",
  romance: "爱情片",
  "sci-fi": "科幻片",
  horror: "恐怖片",
  animation: "动画片",
  drama: "剧情片",
  "tv-series": "电视剧",
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const containerRef = useRef<HTMLDivElement>(null);
  const article = articles.find((a) => a.id === Number(id));

  useEffect(() => {
    if (containerRef.current) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">未找到该影视</h1>
          <p className="text-foreground-500 mb-6">抱歉，您要查看的影视资源不存在或已下架。</p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-2.5 rounded-full text-sm font-semibold cursor-pointer"
            style={{ backgroundColor: "oklch(var(--accent-500))", color: "#fff" }}
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = article.relatedMovies
    .map((mid) => articles.find((a) => a.movieId === mid))
    .filter(Boolean);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${article.title} - 丁香花影院`,
    description: article.description.substring(0, 160),
    author: {
      "@type": "Organization",
      name: "丁香花影院",
    },
    publisher: {
      "@type": "Organization",
      name: "丁香花影院",
    },
    datePublished: article.publishDate,
    image: article.ogImageUrl,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.lilaccinema.com/article/${article.id}`,
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `${article.title}在哪里可以免费在线观看？`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `《${article.title}》可以在丁香花影院免费在线观看，支持高清画质流畅播放，无需注册即可直接观看。`,
        },
      },
      {
        "@type": "Question",
        name: `${article.title}的主演和导演是谁？`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `${article.title}由${article.director}执导，主演包括${article.actors.join("、")}。`,
        },
      },
    ],
  };

  return (
    <>
      <Seo
        title={`${article.title} - 丁香花影院 | 在线观看高清完整版`}
        description={`${article.title}，${article.director}执导，${article.actors.slice(0, 3).join("、")}主演。${article.description.substring(0, 120)}。丁香花影院提供${article.title}高清在线观看。`}
        keywords={`${article.title},${article.title}在线观看,${article.title}免费观看,${categoryNames[article.category]},丁香花影院`}
        canonicalUrl={`https://www.lilaccinema.com/article/${article.id}`}
        ogType="article"
        ogImage={article.ogImageUrl}
        schemaJson={{ ...articleSchema, ...faqSchema }}
      />

      <div className="min-h-screen bg-background-50" ref={containerRef}>
        <Navbar />

        <main>
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: categoryNames[article.category] || article.category, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[300px] flex-shrink-0">
                <div className="sticky top-24">
                  <div className="rounded-lg overflow-hidden aspect-[2/3] bg-background-100">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={article.title + " - 丁香花影院在线观看"}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  {article.title}
                </h1>

                <div className="flex items-center flex-wrap gap-3 mb-6">
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-accent-100 text-accent-700 whitespace-nowrap">
                    {categoryNames[article.category]}
                  </span>
                  <span className="text-sm text-foreground-500">{article.year}</span>
                  <span className="text-sm text-foreground-500">{article.duration}</span>
                  <span className="flex items-center gap-1 text-sm">
                    <span className="text-accent-500 font-bold text-lg">{article.rating}</span>
                    <span className="text-foreground-500">/ 10</span>
                  </span>
                </div>

                <div className="flex flex-wrap gap-4 mb-6 text-sm text-foreground-600">
                  <div>
                    <span className="text-foreground-400">导演：</span>
                    <strong>{article.director}</strong>
                  </div>
                  <div>
                    <span className="text-foreground-400">主演：</span>
                    <strong>{article.actors.join(" / ")}</strong>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-md text-xs bg-background-100 text-foreground-600 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="prose max-w-none mb-8">
                  {article.description.split("\n\n").map((paragraph, idx) => (
                    <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {paragraph.trim()}
                    </p>
                  ))}
                </div>

                <div className="flex items-center gap-4">
                  <button
                    className="inline-flex items-center px-8 py-3 rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: "oklch(var(--accent-500))", color: "#fff" }}
                  >
                    <i className="ri-play-circle-line mr-2"></i>
                    立即播放
                  </button>
                  <button className="inline-flex items-center px-6 py-3 rounded-full font-medium text-sm border border-background-300 text-foreground-700 hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line mr-2"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-14">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedArticles.map((ra) =>
                    ra ? (
                      <MovieCard
                        key={ra.id}
                        id={ra.id}
                        title={ra.title}
                        category={ra.category}
                        year={ra.year}
                        rating={ra.rating}
                        imageUrl={ra.imageUrl}
                      />
                    ) : null
                  )}
                </div>
              </section>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}